import { useLocation, useNavigate } from "@solidjs/router";
import {
  Accessor,
  Setter,
  createSignal,
  onMount,
  Show,
  Switch,
  Match,
} from "solid-js";
import { Product } from "~/server/types/search";
import { zIndexes } from "~/z_indexes";
import { BalancePill } from "./header_telescope_components/balance_pill";
import { ProfileDropdown } from "./header_telescope_components/profile_dropdown";
import { SearchOverlay } from "~/components/search/search_overlay";
import { PlusIcon } from "~/components/search/search_icons";
import { useWebStoreContext } from "~/context/webstore_context";
import { getSearchResults } from "~/components/search/search";
import { config } from "~/data/config";
import { openModalV2 } from "~/shared_states/modal";
import { RedeemCodeModalTelescope } from "~/components/webstore_telescope/redeem_code_modal_telescope";
import { LogoutConfirmationModal } from "../logout_confirmation_modal";
import { getMomentsV3PartnerConfig } from "~/data/partner_config";
import { HubbleLogoTelescope } from "../hubble_badge";
import { useTelescopeContext } from "@hubble/telescope";
import { openCoinsCreditedMessageTelescope } from "../welcome_message_telescope";

/**
 * this header will only be used for Webstore
 */

export function HeaderTelescopeWebstore() {
  const navigate = useNavigate();
  const location = useLocation();
  const webStoreContext = useWebStoreContext();

  const headerImageHref = config.paths.homePageUrl;
  const [isMobileSearchActive, setIsMobileSearchActive] = createSignal(false);
  const [isDesktopSearchActive, setIsDesktopSearchActive] = createSignal(false);

  const [isMenuOpen, setisMenuOpen] = createSignal(false);
  const [allBrands, setAllBrands] = createSignal<Product[]>(
    webStoreContext?.storeState.brands ?? []
  );

  const { isHeaderScrolledOut } = useWebStoreContext();

  // Handle scroll to show/hide redeem button
  onMount(async () => {
    // Load brands for search
    setAllBrands(
      webStoreContext?.storeState.brands ??
        (await getSearchResults(
          "",
          { filterOutInactive: false },
          { pageNo: 0, limit: 30 }
        ))
    );
  });

  const showSearchBar = () => {
    // if the number of brands is greater than 20, show the search bar
    return webStoreContext?.storeState.brands?.length > 20;
  };

  const handleLogoClick = () => {
    navigate(headerImageHref);
  };

  const handleSearch = async (query: string): Promise<Product[]> => {
    return await getSearchResults(query, { filterOutInactive: false });
  };

  const isHomePage = () => {
    return location.pathname === "/";
  };

  const handleRedeemClick = () => {
    openModalV2(
      () => (
        <RedeemCodeModalTelescope
          userName={
            webStoreContext.storeState.v4HomePage?.user?.firstName ?? ""
          }
          refreshV4HomePage={webStoreContext.refreshV4HomePage}
          onSuccess={(amount) => {
            openCoinsCreditedMessageTelescope({
              coinsCredited: amount,
              userName:
                webStoreContext.storeState.v4HomePage?.user?.firstName ?? "",
              totalBalance: webStoreContext.storeState.coins ?? 0,
            });
            // Success callback - can show success message if needed
          }}
        />
      ),
      {
        size: "small",
      }
    );
  };

  const getTncUrl = () => {
    return "https://www.myhubble.money/terms-and-conditions";
  };

  const handleProfileAction = async (
    action: "my-gift-cards" | "help" | "terms" | "logout"
  ) => {
    switch (action) {
      case "my-gift-cards":
        navigate("/profile");
        break;
      case "help":
        webStoreContext.openContactSupport();
        break;
      case "terms":
        window.open(getTncUrl(), "_blank");
        break;
      case "logout":
        setisMenuOpen(false);
        openModalV2(
          () => (
            <LogoutConfirmationModal
              onLogout={() => {
                webStoreContext.onLogout({
                  onLogout: () => {},
                });
              }}
            />
          ),
          {
            size: "small",
          }
        );
        break;
    }
  };

  const displayName = () => {
    return webStoreContext.storeState.v4HomePage?.user?.firstName || "";
  };
  const displayContact = () => {
    return (
      webStoreContext.storeState.v4HomePage?.user?.email ||
      webStoreContext.storeState.v4HomePage?.user?.phoneNumber ||
      ""
    );
  };
  const balance = () => {
    return webStoreContext.storeState.coins ?? 0;
  };

  const logoUrl = () => {
    return (
      getMomentsV3PartnerConfig(webStoreContext.getClientId()).logoUrl ??
      "https://s3.ap-south-1.amazonaws.com/assets.hubble/web-assets/hubble_full.svg"
    );
  };

  const configBgColor = () => {
    return getMomentsV3PartnerConfig(webStoreContext.getClientId())
      .headerBgColor;
  };

  const showSearchBarOnMobile = () => {
    // show search bar on mobile if on home page or category page
    return location.pathname === "/" || location.pathname.includes("category");
  };

  const { components } = useTelescopeContext();

  return (
    <>
      {/* Main Header */}
      <div
        class="sticky top-0 w-full bg-background-normal-primary"
        style={{
          "z-index": zIndexes.header,
          "background-color": configBgColor(),
        }}
      >
        <div class="flex w-full flex-col items-center">
          {/* purpose for adding this div is for fixing -> "on navigation to other pages on ios, the page opens in scrolled state" */}
          <div
            class={`pointer-events-none absolute left-0  right-0 top-20  h-screen  opacity-0 `}
          />

          <div class="flex w-full flex-col items-center">
            <div class="flex w-full max-w-[1024px] items-center justify-between px-3 py-3">
              {/* Logo */}
              <button onClick={handleLogoClick}>
                <HubbleLogoTelescope class="!h-[20px]" />
              </button>
              {/* Search Bar - Centered */}
              <Show when={showSearchBar()}>
                <div class="absolute left-1/2 top-1/2 hidden w-[360px] -translate-x-1/2 -translate-y-1/2 sm:block">
                  <SearchOverlay
                    isActive={isDesktopSearchActive as Accessor<boolean>}
                    setIsActive={setIsDesktopSearchActive}
                    searchBrands={allBrands}
                    totalBrandsCount={400}
                    onSearch={handleSearch}
                    onBrandClick={(product) =>
                      navigate(`/buy-gift-card/${product.brandKey}`)
                    }
                  />
                </div>
              </Show>
              {/* Right Section */}
              <Switch>
                <Match when={webStoreContext.storeState.isLoggedIn}>
                  <div class="flex items-center gap-2">
                    <Switch>
                      <Match when={webStoreContext.storeState.coins == 0}>
                        <components.Button
                          text="Redeem"
                          onClick={handleRedeemClick}
                          variant="primary"
                          style="neutral"
                          size={32}
                          LeadIcon={PlusIcon}
                        />
                      </Match>
                      <Match when={true}>
                        <BalancePill
                          balance={balance()}
                          showRedeemButton={true}
                          onRedeemClick={handleRedeemClick}
                        />
                      </Match>
                    </Switch>
                    <ProfileDropdown
                      showLogout={true}
                      showHelp={
                        getMomentsV3PartnerConfig(webStoreContext.getClientId())
                          .showContactSupport !== false
                      }
                      isOpen={isMenuOpen}
                      setIsOpen={setisMenuOpen}
                      userName={displayName()}
                      userContact={displayContact()}
                      onAction={handleProfileAction}
                    />
                  </div>
                </Match>
                <Match when={!webStoreContext.storeState.isLoggedIn}>
                  <LoginButton />
                </Match>
              </Switch>
            </div>

            {/* Bottom stroke */}
            <div
              class=" h-px w-full bg-stroke-1 "
              classList={{
                "hidden sm:block ": showSearchBarOnMobile(),
                block: !showSearchBarOnMobile(),
              }}
            />
          </div>

          {/* Mobile Layout */}
          <div
            class="w-full flex-col "
            classList={{
              "flex sm:hidden": showSearchBarOnMobile(),
              hidden: !showSearchBarOnMobile(),
            }}
          >
            <Show when={showSearchBar()}>
              <div class="flex w-full flex-col gap-3 p-3">
                {/* Second Row: Search Bar */}
                <SearchOverlay
                  isActive={isMobileSearchActive}
                  setIsActive={setIsMobileSearchActive}
                  searchBrands={allBrands}
                  totalBrandsCount={400}
                  onSearch={handleSearch}
                  onBrandClick={(product) =>
                    navigate(`/buy-gift-card/${product.brandKey}`)
                  }
                />
              </div>
            </Show>

            {/* Bottom stroke */}
            <div class="h-px w-full bg-stroke-1" />
          </div>
        </div>
      </div>
    </>
  );
}

function LoginButton() {
  const webStoreContext = useWebStoreContext();
  const { components } = useTelescopeContext();
  return (
    <div class="flex items-center gap-2">
      <components.Button
        text="For businesses"
        onClick={() => {
          window.open("https://www.myhubble.money/", "_blank");
        }}
        style="neutral"
        variant="quaternary"
        size={32}
      />
      <components.Button
        text="Log in / Sign up"
        style="neutral"
        variant="primary"
        size={32}
        onClick={() => webStoreContext.loginToWebstore(() => {})}
      />
    </div>
  );
}
